import * as Sentry from '@sentry/react';

export const unifiedModuleColors = {
  white: 'white',
  grey: 'grey',
  lightBlue: 'light-blue',
  mistyBlue: 'misty-blue',
  blue: 'blue',
  darkBlue: 'dark-blue',
  black: 'black',
  red: 'red',
  darkRed: 'dark-red',
  lightPurple: 'light-purple',
  lightYellow: 'light-yellow',
  lightGreen: 'light-green',
  quaternaryPurpleXWeak: 'quaternary-purple-xweak',
  primaryBlueXWeak: 'primary-blue-xweak',
  primaryBlueWeak: 'primary-blue-weak',
  primaryBlueStrong: 'primary-blue-strong',
  primaryBlueXStrong: 'primary-blue-xstrong',
  primaryBlueXXStrong: 'primary-blue-xxstrong',
  neutralGreyXWeak: 'neutral-grey-xweak',
  neutralGreyWeak: 'neutral-grey-weak',
  neutralGreyXStrong: 'neutral-grey-xstrong',
  neutralGreyXXStrong: 'neutral-grey-xxstrong',
  neutralPrimary: 'neutral-primary',
  tertiaryYellowXWeak: 'tertiary-yellow-xweak',
  tertiaryYellowWeak: 'tertiary-yellow-weak',
  // the below colors are for the GrowthDiscovery.promoBarBannerColourExperiment. Please do not use them for other purposes.
  experimentalColorBlueAbstractGradient: 'experimental-color-blue-abstract-gradient',
  experimentalColorPinkAbstractGradient: 'experimental-color-pink-abstract-gradient',
  experimentalColorExpressiveYellow: 'experimental-color-expressive-yellow',
};

const COLOR_MAP: Record<string, string> = {
  [unifiedModuleColors.white]: 'var(--cds-color-neutral-background-primary)',
  [unifiedModuleColors.grey]: 'var(--cds-color-neutral-background-primary-weak)',
  [unifiedModuleColors.lightBlue]: 'var(--cds-color-emphasis-primary-background-xweak)',
  [unifiedModuleColors.mistyBlue]: 'var(--cds-color-emphasis-primary-background-weak)',
  [unifiedModuleColors.blue]: 'var(--cds-color-emphasis-primary-background-strong)',
  [unifiedModuleColors.darkBlue]: 'var(--cds-color-emphasis-primary-background-xxstrong)',
  [unifiedModuleColors.black]: 'var(--cds-color-neutral-primary)',
  [unifiedModuleColors.red]: 'var(--cds-color-feedback-error)',
  [unifiedModuleColors.darkRed]: 'var(--cds-color-red-800)',
  [unifiedModuleColors.lightPurple]: `var(--cds-color-callouts-background-secondary-weak)`,
  [unifiedModuleColors.lightYellow]: `var(--cds-color-callouts-background-tertiary-weak)`,
  [unifiedModuleColors.lightGreen]: `var(--cds-color-feedback-background-success-weak)`,
  [unifiedModuleColors.quaternaryPurpleXWeak]: `var(--cds-color-emphasis-quaternary-background-xweak)`,
  [unifiedModuleColors.primaryBlueXWeak]: `var(--cds-color-emphasis-primary-background-xweak)`,
  [unifiedModuleColors.primaryBlueWeak]: `var(--cds-color-emphasis-primary-background-weak)`,
  [unifiedModuleColors.primaryBlueStrong]: `var(--cds-color-emphasis-primary-background-strong)`,
  [unifiedModuleColors.primaryBlueXStrong]: `var(--cds-color-emphasis-primary-background-xstrong)`,
  [unifiedModuleColors.primaryBlueXXStrong]: `var(--cds-color-emphasis-primary-background-xxstrong)`,
  [unifiedModuleColors.neutralGreyWeak]: `var(--cds-color-emphasis-neutral-background-weak)`,
  [unifiedModuleColors.neutralGreyXWeak]: `var(--cds-color-emphasis-neutral-background-xweak)`,
  [unifiedModuleColors.neutralGreyXStrong]: `var(--cds-color-emphasis-neutral-background-xstrong)`,
  [unifiedModuleColors.neutralGreyXXStrong]: `var(--cds-color-emphasis-neutral-background-xxstrong)`,
  [unifiedModuleColors.neutralPrimary]: `var(--cds-color-neutral-background-primary)`,
  [unifiedModuleColors.tertiaryYellowXWeak]: `var(--cds-color-emphasis-tertiary-background-xweak)`,
  [unifiedModuleColors.tertiaryYellowWeak]: `var(--cds-color-emphasis-tertiary-background-weak)`,
  [unifiedModuleColors.experimentalColorExpressiveYellow]: `var(--cds-color-yellow-50)`,
  [unifiedModuleColors.experimentalColorBlueAbstractGradient]: `linear-gradient(87deg, #3587FC 2.18%, #0048B0 97.23%)`,
  [unifiedModuleColors.experimentalColorPinkAbstractGradient]: `linear-gradient(90deg, #C429A8 0.03%, #3587FC 99.95%)`,
};

export function getColorFromColorName(colorName?: string | null) {
  if (!colorName || colorName === 'none') {
    return null;
  }

  return COLOR_MAP[colorName] ?? null;
}

export function isInvertedColor(colorName: string) {
  return [
    'black',
    'dark-blue',
    'blue',
    'red',
    'dark-red',
    'neutral-grey-xstrong',
    'neutral-grey-xxstrong',
    'primary-blue-strong',
    'primary-blue-xstrong',
    'primary-blue-xxstrong',
    'experimental-color-pink-abstract-gradient',
    'experimental-color-blue-abstract-gradient',
  ].includes(colorName);
}

export function getFgColorFromBgColorName(colorName: string) {
  if (isInvertedColor(colorName)) {
    return 'var(--cds-color-neutral-primary-invert)';
  } else {
    return 'var(--cds-color-neutral-primary)';
  }
}

export const TYPOGRAPHY_LIGHT_COLOR = 'var(--cds-color-neutral-primary-invert)';
export const TYPOGRAPHY_DARK_COLOR = 'var(--cds-color-neutral-primary)';

/**
 * This is a rather dangerous function that parses and rotates linear gradients.
 *
 * In an ideal world we could apply the gradient to a pseudo element and then rotate it to avoid risky logic.
 * This is currently problematic because dimensions are maintained from before the rotation so a rectangle being rotated
 * becomes cropped. In the future we could use units like cqw/cqh to enforce dimensions on a rotated element and remove this function.
 *
 * https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Containment/Container_queries
 */
const LINEAR_GRADIENT_REGEX = /([-\d]+)+deg/;
const DIRECTION_GRADIENT_REGEX = /to (left|right|bottom|top)/;
const HAS_DEGREE_REGEX = /\d+deg/;
const DIRECTIONS_ARRAY = ['top', 'right', 'bottom', 'left', 'top'] as const;

export function rotateLinearGradient(gradient: string): string | undefined {
  try {
    const degreeMatch = gradient.match(LINEAR_GRADIENT_REGEX);
    if (degreeMatch?.[1]) {
      return gradient.replace(degreeMatch[1], (parseInt(degreeMatch[1], 10) + 90).toString());
    }

    const directionMatch = gradient.match(DIRECTION_GRADIENT_REGEX);
    if (directionMatch?.[1]) {
      const nextDirection = DIRECTIONS_ARRAY[DIRECTIONS_ARRAY.indexOf(directionMatch[1]) + 1];
      // This is safe because the REGEX capture values are hardcoded to the array values.
      return gradient.replace(directionMatch[1], nextDirection as string);
    }

    // Handle default case: no direction specified defaults to "to bottom", rotate to "to left"
    if (
      gradient.includes('linear-gradient(') &&
      !HAS_DEGREE_REGEX.test(gradient) &&
      !DIRECTION_GRADIENT_REGEX.test(gradient)
    ) {
      return gradient.replace('linear-gradient(', 'linear-gradient(to left, ');
    }

    return gradient;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { message: `[Gradient Rotation] Utility function for rotating the gradient: {${gradient}}` },
    });
  }

  return undefined;
}
